/*
 Designed and developed by Richard Nesnass

 This file is part of SL+.

 SL+ is free software: you can redistribute it and/or modify
 it under the terms of the GNU Affero General Public License as published by
 the Free Software Foundation, either version 3 of the License, or
 (at your option) any later version.

 GPL-3.0-only or GPL-3.0-or-later

 SL+ is distributed in the hope that it will be useful,
 but WITHOUT ANY WARRANTY; without even the implied warranty of
 MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 GNU Affero General Public License for more details.

 You should have received a copy of the GNU Affero General Public License
 along with SL+.  If not, see <http://www.gnu.org/licenses/>.
 */

import { APIResponseCode, AuthType } from './common/interfaces'

const serverHost = import.meta.env.VITE_SERVER_HOST
const serverPort = import.meta.env.VITE_SERVER_PORT
let baseUrl = `${serverHost}`
// Local development: browser SPA on Vite (8080) proxies /api and /auth to the server port.
// Mobile/LAN clients talk to the server port directly.
if (import.meta.env.DEV && serverHost && (serverHost.includes('localhost') || serverHost.includes('10.0.0.') || serverHost.includes('192.168.'))) {
  if (serverHost.includes('localhost')) {
    baseUrl = `${serverHost}:${import.meta.env.VITE_CLIENT_PORT || '8080'}`
  } else {
    baseUrl = `${serverHost}:${serverPort}`
  }
}
let appVersion: string = document.documentElement.getAttribute('data-appversion') || ''
// The web-based app will always be the latest version, set the version directly from .env
// If not built with Cordova, 'data-appversion' will === '%%VERSION%%'
if (appVersion === '%%VERSION%%') appVersion = import.meta.env.VITE_APP_VERSION || ''
export { appVersion, baseUrl }

export const urlRegex = /https?:\/\/(www\+)?([-a-zA-Z]\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/gim
export const clientBaseUrl = `${serverHost}`
export const userRolesString = import.meta.env.VITE_APP_USER_ROLES || ''
export const userRoles = userRolesString.split(',')
export const consentStatesString = import.meta.env.VITE_APP_CONSENT_STATES || ''
export const consentStates = consentStatesString.split(',')
export const projectNamesString = import.meta.env.VITE_APP_PROJECT_NAMES || ''
export const projectNames = projectNamesString.split(',') || []
export const projectTypesString = import.meta.env.VITE_APP_PROJECT_TYPES || ''
export const projectTypes = projectTypesString.split(',') || []
export const masteryBaselineID = 'mastery-baseline'
export const mqttReconnectAttempts = 5
export const deviceType: string = window.location.protocol == 'file:' ? 'mobile' : 'web'
export const tabletBreakpoint = 1280
export const phoneBreakpoint = 768

export const taskColours = ['#A861A6', '#F84016', '#009F4B', '#A9BD50', '#FFDE01']

export const zoomLevels = reactive([0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3])
export const zoomLevelsInverse = reactive([4, 2, 1 / 0.75, 1, 1 / 1.25, 1 / 1.5, 1 / 1.75, 1 / 2, 1 / 2.25, 1 / 2.5, 1 / 2.75, 1 / 3])

export enum ScreensizeType {
  desktop = 'desktop',
  tablet = 'tablet',
  phone = 'phone',
}

export enum UserRole {
  user = 'user',
  monitor = 'monitor',
  admin = 'admin',
  logs = 'logs',
}

export enum LanguageCodes {
  iv = 'iv', // Invariant code
  no = 'no',
  en = 'en',
}
export enum LanguageNames {
  ar = 'العربية', // Arabic
  bs = 'bosanski', // Bosnian
  nb_NO = 'Norsk Bokmål',
  no = 'Norsk',
  /*  nn_NO = 'Norsk Nynorsk', */
  en = 'English',
  ku = 'Soranî', // Kurdish
  pl = 'Polski', // Polish
  so = 'af Soomaali', // Somali
  /*   sq = 'shqip', // Albanian */
  ta = 'தமிழ்', // Tamil
  tr = 'Türkçe',
  ur = 'اردو', // Urdu
  /*  vi = 'tiếng việt', // Vietnamese */
}

export const acceptedStates = [...Object.values(APIResponseCode)]

export enum CreatorType {
  ANONYMOUS = 'ANONYMOUS',
  REGISTERED = 'REGISTERED',
}

export enum ButtonType {
  REGULAR = 0,
  ALTERNATE = 1,
  SOFT = 2,
}

export enum AttachmentType {
  none = 'NONE',
  image = 'IMAGE',
  external = 'EXTERNAL',
}

export enum GroupType {
  ZOOM = 'ZOOM',
  CANVAS = 'CANVAS',
  DATAPORTEN = 'DATAPORTEN',
}

export enum FilterType {
  TAGS = 'tags',
  NOTAGS = 'notags',
  USERS = 'users',
  BOARDS = 'boards',
  HIGHLIGHTS = 'highlights',
  TIME = 'time',
  UPDATE = 'updated',
}

export enum WallSelectionType {
  ALL = 'all walls',
  SHARED = 'shared walls',
  TRASHED = 'trashed walls',
  MINE = 'my walls',
  TEMPLATES = 'templates',
}

export enum PermissionLevel {
  Owner = 'owner',
  Organize = 'organize',
  Join = 'join',
  Copy = 'copy',
}

export enum ColorType {
  User = 'user',
  Board = 'board',
  Organization = 'organisation',
  Any = 'any',
}

export enum LoginType {
  Google = 'google',
  Feide = 'feide',
  PIN = 'pin',
  SAML = 'saml',
}

export enum ParcelType {
  /*
    Sequence:   CONNECT to wall
      Client [connecting]
        1. Client -> (route call) -> Server [connect the user, create Parcel]
        2. Server -> (MQTT Parcel broadcast) => All Clients
        2. Server -> (success response) -> Client

    Sequence:   DISCONNECT from wall
      Client [create Parcel]
        1. Client -> (MQTT Parcel broadcast) => All Clients including Server
        2. Server [remove client]

    Sequence:   CREATE
      Client [makes a change to data]
        1. Client -> (route call) -> Server [persist the change]
        2. Server -> (success response with ID) -> Client [create Parcel]
        3. Client -> (MQTT Parcel broadcast) => All Clients

    Sequence:   UPDATE, DELETE, NO-PERSISTANCE
      Client [makes a change to data, create Parcel]
        1. Client -> (route call) -> Server [persist the change]
        1. Client -> (MQTT Parcel broadcast) => All Clients
        2. Server -> (success response) -> Client

  */
  // ------   Persistance Required ---------
  // all actions:  data: { parcelType, userId, nickname, message/task object }

  // Messages
  MessageCreate = 'mc', // New message
  MessageEdited = 'me', // Edit message
  MessageDelete = 'md', // Message deleted
  MessageBoard = 'mb', // Change to a message's Board entry - highlighted, pin/unpin, detail

  // Tasks
  TaskCreate = 'tc', // New task
  TaskEdited = 'te', // Task settings edited
  TaskDelete = 'td', // Task was removed

  // Walls
  WallEdited = 'we', // Wall settings edited
  WallShared = 'ws', // Wall was shared to user
  WallShareRevoked = 'wr', // Wall share was revoked
  WallShareTemplate = 'wt',
  WallPermission = 'wp', // Sharing permissions changed
  WallDeleted = 'wd',

  ModifyAccess = 'ma',
  ModifyReadOnly = 'ro',
  BoardAdded = 'ba',
  UserContribution = 'ut',
  // ------ No Persistance ---------
  // all actions:  data: { parcelType, userId, nickname, ?wallId, ?taskId }

  UserDisconnect = 'ud', // Sent out by the disconnecting user before leaving
  UserConnect = 'uc', // Sent out by the connecting user after connecting
  UserLogout = 'uo', // Sent when user logs out
  UserLogin = 'ui', // Sent when user logs in
  TaskMove = 'tm', // Sent out only by a Wall's administrator, should trigger clients to change to given task
  GlobalStatus = 'su', // Sent to update clients with Talkwall global status. Body includes a 'status' object.
}

import Feide from '@/assets/icons/login/feide-icon.svg'
import Google from '@/assets/icons/login/google-icon.svg'
import PIN from '@/assets/icons/play-white.svg'
import { reactive, ref } from 'vue'

export const authMethods = [
  {
    label: 'FEIDE',
    type: LoginType.Feide,
    url: `${baseUrl}/auth/${LoginType.Feide}`,
    serviceName: 'feide',
    icon: Feide,
    name: 'Feide icon',
  },
  {
    label: 'Google',
    type: LoginType.Google,
    url: `${baseUrl}/auth/${LoginType.Google}`,
    serviceName: 'google',
    icon: Google,
    name: 'Google icon',
  },
  {
    label: 'anonymous',
    type: LoginType.PIN,
    url: `${baseUrl}/auth/anonymous`,
    serviceName: 'pin',
    icon: PIN,
    name: 'PIN icon',
  }, // Commented SAML on request :SM
  /*{
    label: 'SAML',
    type: LoginType.SAML,
    url: `${baseUrl}/auth/${LoginType.SAML}`,
    serviceName: 'SAML',
    icon: Feide,
    name: 'Feide icon',
  },*/
] as AuthType[]

export const emojis = [
  '😀',
  '😁',
  '😂',
  '😄',
  '😉',
  '😊',
  '😋',
  '😎',
  '😗',
  '😙',
  '😚',
  '🙂',
  '🤗',
  '🤔',
  '😮',
  '🤐',
  '😯',
  '😪',
  '😫',
  '😴',
  '😌',
  '🤓',
  '💙',
  '💚',
  '💛',
  '💜',
  '🖤',
  '👓',
  '🕶',
  '👔',
  '👕',
  '👖',
  '👘',
  '👚',
  '👛',
  '👜',
  '👝',
  '🛍',
  '🎒',
  '👞',
  '👟',
  '👠',
  '👡',
  '👢',
  '👑',
  '👒',
  '🎩',
  '🎓',
  '⛑',
  '💄',
  '💍',
  '💎',
  '🐵',
  '🐒',
  '🦍',
  '🐶',
  '🐕',
  '🐩',
  '🐺',
  '🦊',
  '🐱',
  '🐈',
  '🦁',
  '🐯',
  '🐅',
  '🐆',
  '🐴',
  '🐎',
  '🦌',
  '🦄',
  '🐮',
  '🐂',
  '🐃',
  '🐄',
  '🐷',
  '🐖',
  '🐗',
  '🐽',
  '🐏',
  '🐑',
  '🐐',
  '🐪',
  '🐫',
  '🐘',
  '🦏',
  '🐭',
  '🐁',
  '🐀',
  '🐹',
  '🐰',
  '🐇',
  '🐿',
  '🦇',
  '🐻',
  '🐨',
  '🐼',
  '🐾',
  '🦃',
  '🐔',
  '🐓',
  '🐣',
  '🐤',
  '🐥',
  '🐦',
  '🐧',
  '🕊',
  '🦅',
  '🦆',
  '🦉',
  '🐸',
  '🐊',
  '🐢',
  '🦎',
  '🐍',
  '🐲',
  '🐉',
  '🐳',
  '🐋',
  '🐬',
  '🐟',
  '🐠',
  '🐡',
  '🦈',
  '🐙',
  '🐚',
  '🦀',
  '🦐',
  '🦑',
  '🦋',
  '🐌',
  '🐛',
  '🐜',
  '🐝',
  '🐞',
  '🕷',
  '🕸',
  '🦂',
  '💐',
  '🌸',
  '💮',
  '🏵',
  '🌹',
  '🥀',
  '🌺',
  '🌻',
  '🌼',
  '🌷',
  '🌱',
  '🌲',
  '🌳',
  '🌴',
  '🌵',
  '🌾',
  '🌿',
  '☘',
  '🍀',
  '🍁',
  '🍂',
  '🍃',
  '🍇',
  '🍈',
  '🍉',
  '🍊',
  '🍋',
  '🍌',
  '🍍',
  '🍎',
  '🍏',
  '🍐',
  '🍒',
  '🍓',
  '🥝',
  '🍅',
  '🥑',
  '🥔',
  '🥕',
  '🌽',
  '🌶',
  '🥒',
  '🍄',
  '🥜',
  '🌰',
  '🍞',
  '🥐',
  '🥖',
  '🥞',
  '🧀',
  '🍖',
  '🍗',
  '🥓',
  '🍔',
  '🍟',
  '🍕',
  '🌭',
  '🌮',
  '🌯',
  '🥙',
  '🥚',
  '🍳',
  '🥘',
  '🍲',
  '🥗',
  '🍿',
  '🍱',
  '🍘',
  '🍙',
  '🍚',
  '🍛',
  '🍜',
  '🍝',
  '🍠',
  '🍢',
  '🍣',
  '🍤',
  '🍥',
  '🍡',
  '🍦',
  '🍧',
  '🍨',
  '🍩',
  '🍪',
  '🎂',
  '🍰',
  '🍫',
  '🍬',
  '🍭',
  '🍮',
  '🍯',
  '🍼',
  '🥛',
  '🍵',
  '🍶',
  '🍾',
  '🍷',
  '🍸',
  '🍹',
  '🍺',
  '🍻',
  '🥂',
  '🍽',
  '🍴',
  '🥄',
  '🔪',
  '🏺',
  '🌍',
  '🌎',
  '🌏',
  '🌐',
  '🗺',
  '🗾',
  '🏔',
  '⛰',
  '🌋',
  '🗻',
  '🏕',
  '🏖',
  '🏜',
  '🏝',
  '🏞',
  '🏟',
  '🏛',
  '🏗',
  '🏘',
  '🏙',
  '🏠',
  '🏡',
  '🏢',
  '⛺',
  '🌁',
  '🌃',
  '🌄',
  '🌅',
  '🌆',
  '🌇',
  '🌉',
  '♨',
  '🌌',
  '🎠',
  '🎡',
  '🎢',
  '💈',
  '🎪',
  '🎭',
  '🖼',
  '🎨',
  '🎰',
  '🚂',
  '🚃',
  '🚄',
  '🚅',
  '🚆',
  '🚇',
  '🚈',
  '🚉',
  '🚊',
  '🚝',
  '🚞',
  '🚋',
  '🚌',
  '🚍',
  '🚎',
  '🚐',
  '🚑',
  '🚒',
  '🚓',
  '🚔',
  '🚕',
  '🚖',
  '🚗',
  '🚘',
  '🚙',
  '🚚',
  '🚛',
  '🚜',
  '🚲',
  '🛴',
  '🛵',
  '🚏',
  '🛣',
  '🛤',
  '⛽',
  '🚨',
  '🚥',
  '🚦',
  '🚧',
  '🛑',
  '⚓',
  '⛵',
  '🛶',
  '🚤',
  '🛳',
  '⛴',
  '🛥',
  '🚢',
  '✈',
  '🛩',
  '🛫',
  '🛬',
  '💺',
  '🚁',
  '🚟',
  '🚠',
  '🚡',
  '🚀',
  '🛰',
  '🛎',
  '🚪',
  '🛌',
  '🛏',
  '🛋',
  '🚽',
  '🚿',
  '⏳',
  '⌚',
  '⏰',
  '⏱',
  '🌑',
  '🌕',
  '🌗',
  '⛅',
  '⛈',
  '🌤',
  '🌥',
  '🌦',
  '🌧',
  '🌨',
  '🌩',
  '🔥',
  '💧',
  '🌊',
  '🎃',
  '🎄',
  '🎆',
  '🎇',
  '✨',
  '🎈',
  '🎉',
  '🎊',
  '🎋',
  '🎍',
  '🎎',
  '🎏',
  '🎐',
  '🎑',
  '🎀',
  '🎁',
  '🎗',
  '🎟',
  '🎫',
  '🎖',
  '🏆',
  '🏅',
  '🥇',
  '🥈',
  '🥉',
  '⚽',
  '⚾',
  '🏀',
  '🏐',
  '🏈',
  '🏉',
  '🎾',
  '🎱',
  '🎳',
  '🏏',
  '🏑',
  '🏒',
  '🏓',
  '🏸',
  '🥊',
  '🥋',
  '🥅',
  '🎯',
  '⛳',
  '⛸',
  '🎣',
  '🎽',
  '🎿',
  '🎮',
  '🕹',
  '🎲',
  '🃏',
  '🀄',
  '🎴',
  '🎤',
  '🎧',
  '📻',
  '🎷',
  '🎸',
  '🎹',
  '🎺',
  '🎻',
  '🥁',
  '🏮',
  '📔',
  '📕',
  '📖',
  '📗',
  '📘',
  '📙',
  '📚',
  '📓',
  '📒',
  '📃',
  '📜',
  '📄',
  '📰',
  '🗞',
]
